/**
 * Inner World API hook — progress, quests, submissions, council.
 */
import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { logError } from '@/lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useInnerWorldProgress = () => {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProgress = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/inner-world/progress`);
      setProgress(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not reach the Inner World');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProgress(); }, [fetchProgress]);

  const completeOnboarding = useCallback(async () => {
    await axios.post(`${API}/inner-world/onboarding-complete`);
    setProgress((p) => (p ? { ...p, onboarding_seen: true } : p));
  }, []);

  const selectRealm = useCallback(async (realm) => {
    await axios.post(`${API}/inner-world/select-realm`, { realm });
    setProgress((p) => (p ? { ...p, current_realm: realm } : p));
  }, []);

  return { progress, loading, error, fetchProgress, completeOnboarding, selectRealm };
};

export const useQuest = (realm) => {
  const [questData, setQuestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/inner-world/quest/${realm}`);
        if (!cancelled) setQuestData(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'The path is quiet right now');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [realm]);

  const submitQuest = useCallback(async (payload) => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/inner-world/quest/submit`, payload);
      setResult(res.data);
      return res.data;
    } finally {
      setSubmitting(false);
    }
  }, []);

  const toggleKeepsake = useCallback(async (entryId, include) => {
    await axios.patch(`${API}/inner-world/entries/${entryId}/keepsake`, { include });
  }, []);

  return { questData, loading, error, submitting, result, submitQuest, toggleKeepsake };
};

export const useInnerHome = () => {
  const [home, setHome] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/inner-world/home`)
      .then((res) => setHome(res.data))
      .catch((err) => logError('Inner home fetch failed:', err?.response?.status))
      .finally(() => setLoading(false));
  }, []);

  return { home, loading };
};

export const useKeepsakes = () => {
  const [keepsakes, setKeepsakes] = useState([]);
  const [includedCount, setIncludedCount] = useState(0);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/inner-world/keepsakes`),
      axios.get(`${API}/inner-world/progress`),
    ])
      .then(([kRes, pRes]) => {
        setKeepsakes(kRes.data.keepsakes || []);
        setIncludedCount(kRes.data.included_entries_count || 0);
        setProgress(pRes.data);
      })
      .catch((err) => logError('Keepsakes fetch failed:', err?.response?.status))
      .finally(() => setLoading(false));
  }, []);

  const downloadKeepsake = useCallback(async (keepsake) => {
    const res = await axios.get(`${API}/inner-world/keepsakes/${keepsake.id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `book-of-becoming-${keepsake.generated_at?.slice(0, 10)}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }, []);

  return { keepsakes, includedCount, progress, loading, downloadKeepsake };
};

export const useCouncil = () => {
  const [members, setMembers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);

  const fetchCouncil = useCallback(async () => {
    try {
      const [mRes, rRes] = await Promise.all([
        axios.get(`${API}/inner-world/council`),
        axios.get(`${API}/inner-world/council/reviews`),
      ]);
      setMembers(mRes.data.members || []);
      setReviews(rRes.data.reviews || []);
    } catch (err) {
      // keep prior state
      logError('Council fetch failed:', err?.response?.status);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCouncil(); }, [fetchCouncil]);

  const resolveReview = useCallback(async (reviewId, action, targetMemberId) => {
    await axios.post(`${API}/inner-world/council/reviews/${reviewId}/resolve`, {
      action,
      target_member_id: targetMemberId || null,
    });
    await fetchCouncil();
  }, [fetchCouncil]);

  const openCompanion = useCallback(async (companion) => {
    setSelected(companion);
    setEntriesLoading(true);
    try {
      const res = await axios.get(`${API}/inner-world/council/${companion.id}/entries`);
      setEntries(res.data.entries || []);
    } catch (err) {
      logError('Companion entries fetch failed:', err?.response?.status);
      setEntries([]);
    } finally {
      setEntriesLoading(false);
    }
  }, []);

  const closeCompanion = useCallback(() => { setSelected(null); setEntries([]); }, []);

  return { members, reviews, loading, selected, entries, entriesLoading, openCompanion, closeCompanion, resolveReview };
};
